import { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, getDocs, deleteDoc } from 'firebase/firestore';

const InventoryManagement = ({ user }) => {
  const [inventory, setInventory] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    unit: '',
    currentStock: '',
    lowThreshold: '',
    avgCost: 0,
    available: true
  });
  const [editItemId, setEditItemId] = useState(null);

  // Modal State for Receiving Stock
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveItemId, setReceiveItemId] = useState(null);
  const [receiveCurrentStock, setReceiveCurrentStock] = useState(0);
  const [receiveAvgCost, setReceiveAvgCost] = useState(0);
  const [receiveQty, setReceiveQty] = useState('');
  const [receiveTotalCost, setReceiveTotalCost] = useState('');

  useEffect(() => {
    if (user) {
      fetchInventory();
    }
  }, [user]);

  // Fetch Inventory
  const fetchInventory = async () => {
    const snapshot = await getDocs(collection(db, 'inventory'));
    setInventory(snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        currentStock: parseInt(data.currentStock) || 0,
        lowThreshold: parseInt(data.lowThreshold) || 0,
        avgCost: parseFloat(data.avgCost) || 0
      };
    }));
  };

  // Calculate Low Inventory Items
  const lowInventoryItems = inventory.filter(item => Number(item.currentStock) < Number(item.lowThreshold));

  // Add/Edit Inventory Item
  const saveItem = async () => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    const itemData = {
      name: newItem.name,
      category: newItem.category,
      unit: newItem.unit,
      currentStock: parseInt(newItem.currentStock) || 0,
      lowThreshold: parseInt(newItem.lowThreshold) || 0,
      avgCost: parseFloat(newItem.avgCost) || 0,
      available: newItem.available,
      updatedAt: new Date()
    };
    try {
      if (editItemId) {
        await updateDoc(doc(db, 'inventory', editItemId), itemData);
        setEditItemId(null);
      } else {
        await setDoc(doc(db, 'inventory', newItem.name.toLowerCase().replace(/\s/g, '_')), itemData);
      }
      setNewItem({
        name: '',
        category: '',
        unit: '',
        currentStock: '',
        lowThreshold: '',
        avgCost: 0,
        available: true
      });
      fetchInventory();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      alert('Failed to save item: ' + error.message);
    }
  };

  // Edit Inventory Item
  const editItem = (item) => {
    setEditItemId(item.id);
    setNewItem({
      name: item.name,
      category: item.category,
      unit: item.unit,
      currentStock: item.currentStock.toString(),
      lowThreshold: item.lowThreshold.toString(),
      avgCost: item.avgCost,
      available: item.available
    });
  };

  // Open Receive Modal
  const openReceiveModal = (itemId, currentStock, avgCost) => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    setReceiveItemId(itemId);
    setReceiveCurrentStock(currentStock);
    setReceiveAvgCost(avgCost);
    setReceiveQty('');
    setReceiveTotalCost('');
    setShowReceiveModal(true);
  };

  // Handle Receive Stock
  const handleReceiveStock = async () => {
    if (!receiveQty || !receiveTotalCost || isNaN(receiveQty) || isNaN(receiveTotalCost)) {
      alert('Please enter valid quantity and total cost.');
      return;
    }
    const qty = parseInt(receiveQty);
    const cost = parseFloat(receiveTotalCost);
    const newStock = receiveCurrentStock + qty;
    const totalValueBefore = receiveCurrentStock * receiveAvgCost;
    const totalValueReceived = cost;
    const newAvgCost = (totalValueBefore + totalValueReceived) / newStock;
    try {
      await updateDoc(doc(db, 'inventory', receiveItemId), {
        currentStock: newStock,
        avgCost: newAvgCost,
        updatedAt: new Date()
      });
      setShowReceiveModal(false);
      fetchInventory();
    } catch (error) {
      console.error('Error receiving stock:', error);
      alert('Failed to receive stock: ' + error.message);
    }
  };

  // Deplete Inventory (Remove from Stock)
  const depleteItem = async (itemId, currentStock) => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    const qtyDepleted = prompt('Enter quantity to deplete:');
    if (qtyDepleted && !isNaN(qtyDepleted)) {
      const qty = parseInt(qtyDepleted);
      const newStock = Math.max(0, currentStock - qty);
      try {
        await updateDoc(doc(db, 'inventory', itemId), {
          currentStock: newStock,
          updatedAt: new Date()
        });
        fetchInventory();
      } catch (error) {
        console.error('Error depleting stock:', error);
        alert('Failed to deplete stock: ' + error.message);
      }
    }
  };

  // Delete Inventory Item
  const deleteItem = async (itemId) => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    try {
      await deleteDoc(doc(db, 'inventory', itemId));
      fetchInventory();
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      alert('Failed to delete item: ' + error.message);
    }
  };

  return (
    <div>
      <h2>Inventory Management</h2>
      {/* Low Inventory Notifications */}
      {lowInventoryItems.length > 0 && (
        <div className="summary-section">
          <h3>Low Inventory Alerts</h3>
          <ul>
            {lowInventoryItems.map(item => (
              <li key={item.id}>
                {item.name}: {item.currentStock} {item.unit} remaining (Threshold: {item.lowThreshold} {item.unit})
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="inventory-section">
        <h3>{editItemId ? 'Edit Item' : 'Add New Item'}</h3>
        <div className="inventory-form">
          <input
            value={newItem.name}
            onChange={e => setNewItem({ ...newItem, name: e.target.value })}
            placeholder="Item Name"
          />
          <input
            value={newItem.category}
            onChange={e => setNewItem({ ...newItem, category: e.target.value })}
            placeholder="Category"
          />
          <input
            value={newItem.unit}
            onChange={e => setNewItem({ ...newItem, unit: e.target.value })}
            placeholder="Unit (e.g., Oz)"
          />
          <input
            value={newItem.currentStock}
            onChange={e => setNewItem({ ...newItem, currentStock: e.target.value })}
            placeholder="Initial Stock"
            type="number"
          />
          <input
            value={newItem.lowThreshold}
            onChange={e => setNewItem({ ...newItem, lowThreshold: e.target.value })}
            placeholder="Low Threshold"
            type="number"
          />
          <div className="checkbox-container">
            <label>Available:</label>
            <input
              type="checkbox"
              checked={newItem.available}
              onChange={e => setNewItem({ ...newItem, available: e.target.checked })}
            />
          </div>
        </div>
        <div className="inventory-form-buttons">
          <button className="save-button" onClick={saveItem}>
            {editItemId ? 'Update' : 'Add'}
          </button>
          {editItemId && (
            <button
              className="cancel-button"
              onClick={() => {
                setEditItemId(null);
                setNewItem({ name: '', category: '', unit: '', currentStock: '', lowThreshold: '', avgCost: 0, available: true });
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      <h3>Current Inventory</h3>
      <div className="inventory-grid">
        {inventory.map(item => (
          <div
            key={item.id}
            className={`inventory-card ${Number(item.currentStock) < Number(item.lowThreshold) ? 'low-stock' : ''}`}
          >
            <h4>{item.name}</h4>
            <p>Category: {item.category}</p>
            <p>Stock: {item.currentStock} {item.unit}</p>
            <p>Avg Cost: ${item.avgCost.toFixed(2)}</p>
            <p>Low Threshold: {item.lowThreshold} {item.unit}</p>
            <p>Status: {item.available ? 'Available' : 'Unavailable'}</p>
            <div className="actions">
              <button className="edit-button" onClick={() => editItem(item)}>
                Edit
              </button>
              <button className="receive-button" onClick={() => openReceiveModal(item.id, item.currentStock, item.avgCost)}>
                Receive
              </button>
              <button className="deplete-button" onClick={() => depleteItem(item.id, item.currentStock)}>
                Deplete
              </button>
              <button className="delete-button" onClick={() => deleteItem(item.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Receive Stock Modal */}
      {showReceiveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Receive Stock</h3>
            <div className="modal-form">
              <input
                type="number"
                value={receiveQty}
                onChange={e => setReceiveQty(e.target.value)}
                placeholder="Quantity to Receive"
              />
              <input
                type="number"
                step="0.01"
                value={receiveTotalCost}
                onChange={e => setReceiveTotalCost(e.target.value)}
                placeholder="Total Cost"
              />
            </div>
            <div className="modal-buttons">
              <button className="modal-save-button" onClick={handleReceiveStock}>
                Confirm
              </button>
              <button className="modal-cancel-button" onClick={() => setShowReceiveModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;